import { useState, useEffect, useRef, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/features/auth/context/AuthContext";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Modal } from "@/shared/components/ui/Modal";
import { companyService } from "@/features/company/services/companyService";
import { profileService, userManagementService, visaService } from "@/features/profile/services/visaService";
import { sendingOrgService } from "@/features/return-home/services/sendingOrgService";
import { formatDisplayName } from "@/shared/utils/formatters";

const OFFICER_TYPES = ["officer", "admin"];
const PAGE_SIZE = 25;
const SESSION_KEY = "userSearch_state";

interface SearchResult {
  user_id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  company: string;
  company_name?: string;
  batch_no?: string;
  position?: string;
  user_type?: string;
  phone_number?: string;
  country?: string;
  gender?: string;
  age?: number | null;
  sending_org_descr?: string | null;
  passport_expiry?: string | null;
  visa_expiry_date?: string | null;
  visa_type_descr?: string | null;
  is_active?: boolean;
  email?: string;
  last_seen?: string | null;
}

type SortKey = "name" | "company_name" | "batch_no" | "position" | "user_type" | "last_seen" | "status" | "country" | "sending_org" | "age" | "gender" | "visa_type" | "passport_expiry" | "visa_expiry";

// ── Relative-time formatter ──
const formatLastSeen = (iso: string | null | undefined, t: (k: string, o?: Record<string, unknown>) => string): string => {
  if (!iso) return t("settings.last_seen_never");
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60)  return t("settings.last_seen_s", { n: diffSec });
  if (diffSec < 3600)  return t("settings.last_seen_m", { n: Math.floor(diffSec / 60) });
  if (diffSec < 86400) return t("settings.last_seen_h", { n: Math.floor(diffSec / 3600) });
  return t("settings.last_seen_d", { n: Math.floor(diffSec / 86400) });
};
type SortDir = "asc" | "desc";

// ── Expiry helpers ──
const daysUntil = (dateStr: string | null | undefined): number | null => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr);
  expiry.setHours(0, 0, 0, 0);
  return Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const ExpiryBadge = ({ dateStr }: { dateStr: string | null | undefined }) => {
  const { t } = useTranslation();
  const days = daysUntil(dateStr);
  if (days === null) return <span className="text-xs text-gray-400">-</span>;
  if (days <= 0)
    return <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">{t("access.expiry_expired")}</span>;
  if (days <= 30)
    return <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">{t("access.expiry_in_days", { days })}</span>;
  return <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">{t("access.expiry_in_days", { days })}</span>;
};

// ── Multi-select dropdown ──
const MultiSelectDropdown = ({
  label,
  options,
  selected,
  onChange,
  placeholder,
  searchable = true,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  searchable?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-1 relative" ref={ref}>
      <label className="block text-xs font-medium text-gray-500">{label}</label>
      {/* Trigger */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => e.key === "Enter" && setOpen((v) => !v)}
        className="w-full min-h-[38px] px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white flex flex-wrap gap-1 items-center"
      >
        {selected.length === 0 ? (
          <span className="text-gray-400 text-sm py-0.5 flex-1">{placeholder}</span>
        ) : (
          selected.map((v) => {
            const lbl = options.find((o) => o.value === v)?.label ?? v;
            return (
              <span key={v} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs font-medium">
                {lbl}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(v); }}
                  className="hover:text-indigo-900 leading-none"
                >×</button>
              </span>
            );
          })
        )}
        <svg className={`w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {/* Dropdown list */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 flex flex-col max-h-56">
          {searchable && (
            <div className="p-2 border-b border-gray-100 flex-shrink-0">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400 italic">No results</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(opt.value); }}
                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors"
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selected.includes(opt.value) ? "bg-indigo-600 border-indigo-600" : "border-gray-300"}`}>
                    {selected.includes(opt.value) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className={selected.includes(opt.value) ? "text-indigo-700 font-medium" : "text-gray-700"}>{opt.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sortable header cell ──
const SortHeader = ({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  className = "",
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (k: SortKey) => void;
  className?: string;
}) => {
  const active = currentKey === sortKey;
  return (
    <th
      className={`px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 whitespace-nowrap ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <svg className={`w-3 h-3 ${active ? "text-indigo-600" : "text-gray-300"}`} viewBox="0 0 10 14" fill="currentColor">
          <path d={active && currentDir === "asc" ? "M5 0L10 6H0z" : active && currentDir === "desc" ? "M5 14L0 8h10z" : "M5 0L10 6H0zM5 14L0 8h10z"} />
        </svg>
      </span>
    </th>
  );
};

// ── Delete Confirmation Modal ──
const DeleteConfirmModal = ({
  user,
  onConfirm,
  onCancel,
  loading,
}: {
  user: SearchResult | null;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) => {
  const { t } = useTranslation();
  if (!user) return null;
  const fullName = formatDisplayName(user.last_name, user.first_name, user.middle_name);

  return (
    <Modal isOpen={!!user} onClose={onCancel} title={t("settings.delete_user_title")}>
      <div className="space-y-4">
        {/* Warning icon + message */}
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {t("settings.delete_user_heading", { name: fullName })}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {t("settings.delete_user_desc")}
            </p>
          </div>
        </div>

        {/* User summary */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-700 space-y-1">
          <div><span className="text-gray-400">{t("settings.delete_user_label_name")}:</span> {fullName}</div>
          {user.email && <div><span className="text-gray-400">{t("settings.delete_user_label_email")}:</span> {user.email}</div>}
          {user.company_name && <div><span className="text-gray-400">{t("settings.delete_user_label_company")}:</span> {user.company_name}</div>}
          {user.batch_no && <div><span className="text-gray-400">{t("settings.delete_user_label_batch")}:</span> {user.batch_no}</div>}
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end pt-1">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm} loading={loading}>
            {t("settings.delete_user_confirm_btn")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ── Change Password Modal ──
const ChangePasswordModal = ({
  user,
  onConfirm,
  onCancel,
  loading,
}: {
  user: SearchResult | null;
  onConfirm: (newPassword: string) => void;
  onCancel: () => void;
  loading: boolean;
}) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");

  // Reset fields whenever the modal opens for a new user
  useEffect(() => {
    if (user) {
      setNewPassword("");
      setConfirmPassword("");
      setError("");
      setShowNew(false);
      setShowConfirm(false);
    }
  }, [user?.user_id]);

  if (!user) return null;
  const fullName = formatDisplayName(user.last_name, user.first_name, user.middle_name);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError(t("common.alert.password_length_error"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("common.alert.password_match_error"));
      return;
    }
    setError("");
    onConfirm(newPassword);
  };

  return (
    <Modal isOpen={!!user} onClose={onCancel} title={t("settings.reset_pw_modal_title")}>
      <div className="space-y-4">
        {/* Info row */}
        <div className="flex gap-3 items-start">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{fullName}</p>
            {user.email && <p className="text-xs text-gray-400 mt-0.5">{user.email}</p>}
            <p className="text-xs text-gray-500 mt-1">{t("settings.reset_pw_modal_subtitle")}</p>
          </div>
        </div>

        {/* Password fields */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <Input
              label={t("auth.new_password_label")}
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="relative">
            <Input
              label={t("auth.confirm_password_label")}
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="secondary" size="sm" type="button" onClick={onCancel} disabled={loading}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="sm" loading={loading}>
              {t("settings.reset_pw_modal_title")}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export const UserSearchPage = () => {
  const { t, i18n } = useTranslation();
  const { userData } = useAuth();
  const { canWrite } = usePermissions();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");

  // Guard – non-officers can't access
  if (!isOfficer) return <Navigate to="/settings" replace />;

  // ── Restore persisted search state ──
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null"); } catch { return null; } })();

  // ── Search params ──
  const [searchBatch, setSearchBatch] = useState<string>(saved?.searchBatch ?? "");
  const [searchName, setSearchName] = useState<string>(saved?.searchName ?? "");

  // Multi-select filter state
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>(saved?.selectedCompanyIds ?? []);
  const [filterCountries, setFilterCountries] = useState<string[]>(saved?.filterCountries ?? []);
  const [filterSendingOrgs, setFilterSendingOrgs] = useState<string[]>(saved?.filterSendingOrgs ?? []);
  const [filterVisaTypes, setFilterVisaTypes] = useState<string[]>(saved?.filterVisaTypes ?? []);
  const [filterUserTypes, setFilterUserTypes] = useState<string[]>(saved?.filterUserTypes ?? []);
  const [filterPassportWithin, setFilterPassportWithin] = useState<string>(saved?.filterPassportWithin ?? "");
  const [filterVisaWithin, setFilterVisaWithin] = useState<string>(saved?.filterVisaWithin ?? "");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "inactive">(saved?.filterStatus ?? "all");

  // Dropdown option lists
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [sendingOrgs, setSendingOrgs] = useState<{ value: string; label: string }[]>([]);
  const [visaTypes, setVisaTypes] = useState<{ value: string; label: string }[]>([]);

  // Results
  const [allResults, setAllResults] = useState<SearchResult[]>(saved?.allResults ?? []);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState<boolean>(saved?.hasSearched ?? false);

  // Sort & pagination
  const [sortKey, setSortKey] = useState<SortKey>(saved?.sortKey ?? "name");
  const [sortDir, setSortDir] = useState<SortDir>(saved?.sortDir ?? "asc");
  const [page, setPage] = useState<number>(saved?.page ?? 0);

  // ── Filter panel visibility ──
  const [filtersOpen, setFiltersOpen] = useState<boolean>(saved?.filtersOpen ?? true);

  // ── Persist search state to sessionStorage ──
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      searchBatch, searchName,
      selectedCompanyIds, filterCountries, filterSendingOrgs, filterVisaTypes, filterUserTypes,
      filterPassportWithin, filterVisaWithin, filterStatus,
      allResults, hasSearched, sortKey, sortDir, page, filtersOpen,
    }));
  }, [searchBatch, searchName, selectedCompanyIds, filterCountries, filterSendingOrgs, filterVisaTypes, filterUserTypes, filterPassportWithin, filterVisaWithin, filterStatus, allResults, hasSearched, sortKey, sortDir, page, filtersOpen]);

  // Per-row action state
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<SearchResult | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [changePwUser, setChangePwUser] = useState<SearchResult | null>(null);
  const [changePwLoading, setChangePwLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // ── Bootstrap ──
  useEffect(() => {
    companyService.getDropdown().then(setCompanies).catch(console.error);
    visaService.getVisaDropdown(i18n.language || "en").then(setVisaTypes).catch(console.error);
  }, []);

  // ── Reload sending orgs when country selection changes ──
  useEffect(() => {
    if (filterCountries.length === 0) {
      setSendingOrgs([]);
      setFilterSendingOrgs([]);
      return;
    }
    // Single country → filtered list; multiple → fetch each and merge deduped
    const fetches = filterCountries.map((c) => sendingOrgService.getDropdown(c));
    Promise.all(fetches)
      .then((results) => {
        const seen = new Set<string>();
        const merged = results.flat().filter((o: { value: string; label: string }) => {
          if (seen.has(o.value)) return false;
          seen.add(o.value);
          return true;
        });
        setSendingOrgs(merged);
        // Drop any selected orgs that are no longer in the list
        setFilterSendingOrgs((prev) => prev.filter((v) => merged.some((o: { value: string }) => o.value === v)));
      })
      .catch(console.error);
  }, [filterCountries.join(",")]);

  const hasAnyFilter = selectedCompanyIds.length > 0 || !!searchBatch || !!searchName || filterCountries.length > 0 || filterSendingOrgs.length > 0 || filterVisaTypes.length > 0 || filterUserTypes.length > 0 || !!filterPassportWithin || !!filterVisaWithin || filterStatus !== "all";

  // ── Search ──
  const handleSearch = async () => {
    if (!hasAnyFilter) return;
    setSearching(true);
    setHasSearched(true);
    setPage(0);
    setActionError(null);
    try {
      const res = await profileService.searchUsers({
        company:               selectedCompanyIds.length ? selectedCompanyIds.join(",") : undefined,
        batch_no:              searchBatch || undefined,
        name:                  searchName || undefined,
        country:               filterCountries.length ? filterCountries.join(",") : undefined,
        sending_org:           filterSendingOrgs.length ? filterSendingOrgs.join(",") : undefined,
        visa_type:             filterVisaTypes.length ? filterVisaTypes.join(",") : undefined,
        user_type:             filterUserTypes.length ? filterUserTypes.join(",") : undefined,
        passport_expiry_within: filterPassportWithin || undefined,
        visa_expiry_within:    filterVisaWithin || undefined,
        status:                filterStatus,
      });
      const rows = (Array.isArray(res) ? res : res.users || []) as SearchResult[];
      setAllResults(rows.filter((u) => (u.user_type || "").toUpperCase() !== "SOUSER"));
    } catch {
      setAllResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleClear = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSelectedCompanyIds([]);
    setSearchBatch("");
    setSearchName("");
    setFilterCountries([]);
    setFilterSendingOrgs([]);
    setFilterVisaTypes([]);
    setFilterUserTypes([]);
    setFilterPassportWithin("");
    setFilterVisaWithin("");
    setFilterStatus("all");
    setAllResults([]);
    setHasSearched(false);
    setPage(0);
    setActionError(null);
  };

  // ── Sorting ──
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const getValue = (row: SearchResult, key: SortKey): string => {
    switch (key) {
      case "name":
        return formatDisplayName(row.last_name, row.first_name, row.middle_name).toLowerCase();
      case "company_name":
        return (row.company_name || row.company || "").toLowerCase();
      case "batch_no":
        return (row.batch_no || "").toLowerCase();
      case "position":
        return (row.position || "").toLowerCase();
      case "user_type":
        return (row.user_type || "").toLowerCase();
      case "last_seen":
        return row.last_seen ? new Date(row.last_seen).getTime().toString().padStart(20, "0") : "0";
      case "status":
        return row.is_active === false ? "inactive" : "active";
      case "country":
        return (row.country || "").toLowerCase();
      case "sending_org":
        return (row.sending_org_descr || "").toLowerCase();
      case "age":
        return (row.age ?? -1).toString().padStart(4, "0");
      case "gender":
        return (row.gender || "").toLowerCase();
      case "visa_type":
        return (row.visa_type_descr || "").toLowerCase();
      case "passport_expiry":
        return row.passport_expiry ?? "9999-99-99";
      case "visa_expiry":
        return row.visa_expiry_date ?? "9999-99-99";
    }
  };

  const sorted = useMemo(() => {
    const copy = [...allResults];
    copy.sort((a, b) => {
      const va = getValue(a, sortKey);
      const vb = getValue(b, sortKey);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [allResults, sortKey, sortDir]);

  // ── Pagination ──
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Deactivate / Activate ──
  const handleToggleActive = async (user: SearchResult) => {
    setTogglingId(user.user_id);
    setActionError(null);
    try {
      const res = await userManagementService.toggleActive(user.user_id);
      setAllResults((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id ? { ...u, is_active: res.is_active } : u,
        ),
      );
    } catch {
      setActionError(t("settings.error_toggle_active", { name: formatDisplayName(user.last_name, user.first_name, user.middle_name) }));
    } finally {
      setTogglingId(null);
    }
  };

  // ── Delete ──
  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    setActionError(null);
    try {
      await userManagementService.deleteUser(deletingUser.user_id);
      setAllResults((prev) => prev.filter((u) => u.user_id !== deletingUser.user_id));
      setDeletingUser(null);
    } catch {
      setActionError(t("settings.error_delete_user", { name: formatDisplayName(deletingUser.last_name, deletingUser.first_name, deletingUser.middle_name) }));
      setDeletingUser(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Change Password ──
  const handleChangePassword = async (newPassword: string) => {
    if (!changePwUser) return;
    setChangePwLoading(true);
    setActionError(null);
    try {
      await userManagementService.resetUserPassword(changePwUser.user_id, newPassword);
      setChangePwUser(null);
    } catch {
      setActionError(t("settings.error_reset_password", { name: formatDisplayName(changePwUser.last_name, changePwUser.first_name, changePwUser.middle_name) }));
      setChangePwUser(null);
    } finally {
      setChangePwLoading(false);
    }
  };

  const isSelf = (userId: string) => String(userId) === String(userData?.id);

  // ── Export CSV ──
  const handleExportCSV = () => {
    const escape = (val: string | undefined | null) => {
      const s = val ?? "";
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const headers = [
      "Last Name", "First Name", "Middle Name",
      "Email", "Phone",
      "User Type", "Position",
      "Company", "Batch No",
      "Nationality", "Sending Org", "Age", "Gender",
      "Visa Type", "Passport Expiry", "Visa Expiry",
      "Status",
    ];

    const rows = sorted.map((u) => [
      escape(u.last_name),
      escape(u.first_name),
      escape(u.middle_name),
      escape(u.email),
      escape(u.phone_number),
      escape((() => { switch ((u.user_type || "").toUpperCase()) { case "OFFICER": return t("settings.user_type_officer"); case "ADMIN": return t("settings.user_type_admin"); case "SOUSER": return t("settings.user_type_souser"); case "USER": return t("settings.user_type_user"); default: return u.user_type || ""; } })()),
      escape(u.position),
      escape(u.company_name || u.company),
      escape(u.batch_no),
      escape(u.country),
      escape(u.sending_org_descr),
      u.age != null ? String(u.age) : "",
      escape(u.gender),
      escape(u.visa_type_descr),
      escape(u.passport_expiry),
      escape(u.visa_expiry_date),
      u.is_active === false ? "Inactive" : "Active",
    ]);

    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t("settings.officer_search")}</h1>
        <p className="text-xs text-gray-400 mt-0.5">{t("settings.user_mgmt_subtitle")}</p>
      </div>

      {/* ── Search bar ── */}
      {(() => {
        const countryOptions = ["JP","PH","VN","ID","MM","KH","TH","CN","NP","IN","BD","LK"].map((code) => ({
          value: code,
          label: t(`profile.country_${code.toLowerCase()}`, { defaultValue: code }),
        }));
        return (
          <div className="bg-white rounded-lg border border-gray-200 mb-4 md:mb-6">
            {/* Collapsible header */}
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 md:px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                {t("settings.search_btn")}
                {hasAnyFilter && (
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px] font-bold leading-none">
                    {[selectedCompanyIds.length > 0, !!searchBatch, !!searchName, filterCountries.length > 0, filterSendingOrgs.length > 0, filterVisaTypes.length > 0, filterUserTypes.length > 0, !!filterPassportWithin, !!filterVisaWithin, filterStatus !== "all"].filter(Boolean).length}
                  </span>
                )}
              </span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${filtersOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {filtersOpen && <div className="px-3 md:px-4 pb-3 md:pb-4 space-y-3 border-t border-gray-100">
            {/* Row 1: Company, Batch, Name, Country */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <MultiSelectDropdown
                label={t("settings.search_company")}
                options={companies}
                selected={selectedCompanyIds}
                onChange={setSelectedCompanyIds}
                placeholder={t("inquiry.search_company")}
              />

              {/* Batch */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500">{t("settings.search_batch")}</label>
                <input
                  type="text"
                  value={searchBatch}
                  onChange={(e) => setSearchBatch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  placeholder={t("profile.batch_no")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Name */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500">{t("settings.search_name")}</label>
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  placeholder={t("inquiry.search_name")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <MultiSelectDropdown
                label={t("profile.country")}
                options={countryOptions}
                selected={filterCountries}
                onChange={setFilterCountries}
                placeholder={t("access.filter_all")}
              />
            </div>

            {/* Row 2: Sending Org, Visa Type, User Type, Status, Passport within, Visa within */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              {filterCountries.length > 0 ? (
                <MultiSelectDropdown
                  label={t("access.col_sending_org")}
                  options={sendingOrgs}
                  selected={filterSendingOrgs}
                  onChange={setFilterSendingOrgs}
                  placeholder={t("access.filter_all")}
                />
              ) : (
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-400">{t("access.col_sending_org")}</label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-400 bg-gray-50 cursor-not-allowed">
                    {t("profile.sending_org_select_country_first")}
                  </div>
                </div>
              )}

              <MultiSelectDropdown
                label={t("access.col_visa_type")}
                options={visaTypes}
                selected={filterVisaTypes}
                onChange={setFilterVisaTypes}
                placeholder={t("access.filter_all")}
              />

              <MultiSelectDropdown
                label={t("profile.user_type")}
                options={[
                  { value: "USER",    label: t("settings.user_type_user") },
                  { value: "OFFICER", label: t("settings.user_type_officer") },
                ]}
                selected={filterUserTypes}
                onChange={setFilterUserTypes}
                placeholder={t("access.filter_all")}
                searchable={false}
              />

              {/* Status */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500">{t("settings.status_col", "Status")}</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-300 text-sm h-[38px]">
                  {(["all", "active", "inactive"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFilterStatus(s)}
                      className={`flex-1 capitalize transition-colors ${
                        filterStatus === s
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Passport expiry within N days */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500">{t("access.col_passport_expiry")}</label>
                <input
                  type="number"
                  min="0"
                  value={filterPassportWithin}
                  onChange={(e) => setFilterPassportWithin(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  placeholder={t("access.filter_within_days")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {filterPassportWithin && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    {t("access.expiry_filter_officer_note")}
                  </p>
                )}
              </div>

              {/* Visa expiry within N days */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500">{t("access.col_visa_expiry")}</label>
                <input
                  type="number"
                  min="0"
                  value={filterVisaWithin}
                  onChange={(e) => setFilterVisaWithin(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  placeholder={t("access.filter_within_days")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {filterVisaWithin && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    {t("access.expiry_filter_officer_note")}
                  </p>
                )}
              </div>
            </div>

            {/* Buttons row — Search first, then Clear */}
            <div className="flex justify-end gap-2">
              <Button onClick={handleSearch} loading={searching} disabled={!hasAnyFilter} size="sm">
                {t("settings.search_btn")}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleClear}>
                {t("common.clear_all")}
              </Button>
            </div>
          </div>}
          </div>
        );
      })()}

      {/* Action error banner */}
      {actionError && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* ── Results ── */}
      {searching && (
        <div className="py-12"><Spinner /></div>
      )}

      {!searching && hasSearched && allResults.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
          <p className="text-sm text-gray-400 italic">{t("settings.search_no_results")}</p>
        </div>
      )}

      {!searching && allResults.length > 0 && (filterPassportWithin || filterVisaWithin || filterCountries.length > 0 || filterSendingOrgs.length > 0) && (
        <div className="mb-3 flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t("access.expiry_filter_officer_note")}
        </div>
      )}

      {!searching && allResults.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Count + pagination info */}
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {t("settings.search_showing", {
                from: page * PAGE_SIZE + 1,
                to: Math.min((page + 1) * PAGE_SIZE, sorted.length),
                total: sorted.length,
              })}
            </p>
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              {t("settings.export_csv", "Export CSV")} ({sorted.length})
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 w-[40px] min-w-[40px] max-w-[40px] bg-gray-50 md:sticky md:top-0 md:left-0 md:z-20" />
                  <SortHeader label={t("settings.search_name")} sortKey="name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="w-[220px] min-w-[220px] max-w-[220px] bg-gray-50 md:sticky md:top-0 md:left-[40px] md:z-20" />
                  <SortHeader label={t("settings.search_company")} sortKey="company_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="w-[180px] min-w-[180px] max-w-[180px] bg-gray-50 md:sticky md:top-0 md:left-[260px] md:z-20 md:shadow-[2px_0_0_0_#e5e7eb]" />
                  <SortHeader label={t("settings.search_batch")} sortKey="batch_no" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("profile.country")} sortKey="country" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("access.col_sending_org")} sortKey="sending_org" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("access.col_age")} sortKey="age" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("profile.gender")} sortKey="gender" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("profile.position")} sortKey="position" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("profile.user_type")} sortKey="user_type" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("access.col_visa_type")} sortKey="visa_type" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("access.col_passport_expiry")} sortKey="passport_expiry" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("access.col_visa_expiry")} sortKey="visa_expiry" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("settings.last_seen_col")} sortKey="last_seen" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label={t("settings.status_col")} sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t("settings.search_actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((user) => {
                  const isActive = user.is_active !== false;
                  const pinnedBackground = isActive ? "bg-white group-hover:bg-gray-50" : "bg-gray-50 group-hover:bg-gray-50 [&>*]:opacity-75";
                  const self = isSelf(user.user_id);
                  const toggling = togglingId === user.user_id;

                  const passportDays = daysUntil(user.passport_expiry);
                  const visaDays = daysUntil(user.visa_expiry_date);
                  const passportExpired = passportDays !== null && passportDays <= 0;
                  const visaExpired = visaDays !== null && visaDays <= 0;
                  const hasDocWarning = passportExpired || visaExpired;
                  const warningTooltip = [
                    passportExpired ? t("access.warning_passport_expired") : null,
                    visaExpired ? t("access.warning_visa_expired") : null,
                  ].filter(Boolean).join(" · ");

                  return (
                    <tr
                      key={user.user_id}
                      className={`group transition-colors ${!isActive ? "bg-gray-50/60 [&>td:nth-child(n+4)]:opacity-75" : "hover:bg-gray-50"}`}
                    >
                      {/* Warning column */}
                      <td className={`px-2 py-2.5 w-[40px] min-w-[40px] max-w-[40px] text-center md:sticky md:left-0 md:z-10 ${pinnedBackground}`}>
                        {hasDocWarning && (
                          <span
                            title={warningTooltip}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 font-bold text-xs cursor-default select-none"
                          >!</span>
                        )}
                      </td>
                      <td className={`px-3 py-2.5 w-[220px] min-w-[220px] max-w-[220px] whitespace-nowrap md:sticky md:left-[40px] md:z-10 ${pinnedBackground}`}>
                        <div className="max-w-[196px]">
                          <span className="block truncate font-medium text-gray-800">{formatDisplayName(user.last_name, user.first_name, user.middle_name)}</span>
                          {user.email && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[160px]">{user.email}</p>
                          )}
                        </div>
                      </td>
                      <td className={`px-3 py-2.5 w-[180px] min-w-[180px] max-w-[180px] whitespace-nowrap text-gray-600 md:sticky md:left-[260px] md:z-10 md:shadow-[2px_0_0_0_#e5e7eb] ${pinnedBackground}`}>
                        <div className="max-w-[156px] truncate">{user.company_name || user.company || "-"}</div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{user.batch_no || "-"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 text-xs">
                        {user.country ? t(`profile.country_${user.country}`, { defaultValue: user.country }) : "-"}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 text-xs">{user.sending_org_descr || "-"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 text-xs">{user.age != null ? user.age : "-"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 text-xs">
                        {user.gender ? t(`profile.gender_${user.gender.toLowerCase()}`, { defaultValue: user.gender }) : "-"}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{user.position || "-"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          OFFICER_TYPES.includes((user.user_type || "").toLowerCase())
                            ? "bg-indigo-100 text-indigo-700"
                            : "bg-gray-100 text-gray-600"
                        }`}>{
                          (() => {
                            switch ((user.user_type || "").toUpperCase()) {
                              case "OFFICER": return t("settings.user_type_officer");
                              case "ADMIN":   return t("settings.user_type_admin");
                              case "SOUSER":  return t("settings.user_type_souser");
                              case "USER":    return t("settings.user_type_user");
                              default:        return user.user_type || "-";
                            }
                          })()
                        }</span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 text-xs">{user.visa_type_descr || "-"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><ExpiryBadge dateStr={user.passport_expiry} /></td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><ExpiryBadge dateStr={user.visa_expiry_date} /></td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className="text-xs text-gray-500">
                          {formatLastSeen(user.last_seen, t)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-green-500" : "bg-gray-400"}`} />
                          {isActive ? t("settings.status_active") : t("settings.status_inactive")}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-3">

                          {/* ── Safe actions ── */}
                          <div className="inline-flex items-center gap-1">
                            {/* Profile */}
                            <Link
                              to={`/settings/personal-info/${user.user_id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                              title={t("settings.personal_info")}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              {t("settings.btn_profile")}
                            </Link>

                            {/* Visa — hidden for officer-type users */}
                            {!OFFICER_TYPES.includes(user.user_type?.toLowerCase() || "") && (
                              <Link
                                to={`/settings/visa-info/${user.user_id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                                title={t("settings.work_visa_info")}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {t("settings.btn_visa")}
                              </Link>
                            )}

                            {/* Change Password — only for profile_write officers, not self */}
                            {!self && canWrite("profile") && (
                              <button
                                onClick={() => setChangePwUser(user)}
                                title={t("settings.btn_change_password")}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                                {t("settings.btn_change_password")}
                              </button>
                            )}
                          </div>

                          {/* ── Divider + destructive actions (profile_write only, not self) ── */}
                          {!self && canWrite("profile") && (
                            <>
                              <span className="w-px h-5 bg-gray-200 flex-shrink-0" aria-hidden="true" />
                              <div className="inline-flex items-center gap-1">
                                {/* Deactivate / Activate */}
                                <button
                                  onClick={() => handleToggleActive(user)}
                                  disabled={toggling}
                                  title={isActive ? t("settings.tooltip_deactivate") : t("settings.tooltip_reactivate")}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                                    isActive
                                      ? "bg-orange-50 text-orange-700 hover:bg-orange-100"
                                      : "bg-green-50 text-green-700 hover:bg-green-100"
                                  }`}
                                >
                                  {toggling ? (
                                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                  ) : isActive ? (
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                    </svg>
                                  ) : (
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                  {isActive ? t("settings.btn_deactivate") : t("settings.btn_activate")}
                                </button>

                                {/* Delete */}
                                <button
                                  onClick={() => setDeletingUser(user)}
                                  title={t("settings.tooltip_delete_user")}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  {t("common.delete")}
                                </button>
                              </div>
                            </>
                          )}

                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                {t("settings.page_prev")}
              </Button>
              <span className="text-xs text-gray-500">
                {page + 1} / {totalPages}
              </span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                {t("settings.page_next")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        user={deletingUser}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingUser(null)}
        loading={deleteLoading}
      />

      {/* Change password modal */}
      <ChangePasswordModal
        user={changePwUser}
        onConfirm={handleChangePassword}
        onCancel={() => setChangePwUser(null)}
        loading={changePwLoading}
      />
    </div>
  );
};
